import {
	getCategoryCacheUrlPerimeterSSR,
	type CategorizedStorageControlsCache,
} from '@atlassian/browser-storage-controls';

import { cfetch } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

export const MONITORING_ATTRIBUTION = 'backbone';

type StorageType = 'LOCAL_STORAGE' | 'SESSION_STORAGE';

const fetchCategories = async (
	perimeter: string,
	storageType: StorageType,
): Promise<CategorizedStorageControlsCache | undefined> => {
	try {
		const url = getCategoryCacheUrlPerimeterSSR(
			'bifrost' as any,
			perimeter as any,
			storageType as any,
		);
		const res = await cfetch(url, {
			method: 'GET',
			credentials: 'omit',
			headers: { Accept: 'application/json' },
		});
		if (!res.ok) {
			throw new Error(`Cannot load Cookie Consent Categories (${storageType}): ${res.statusText}`);
		}
		return await res.json();
	} catch (e) {
		getMonitoringClient().submitError(e, { attribution: MONITORING_ATTRIBUTION });
		return;
	}
};

export const preloadWebStorageCategories = async (): Promise<void> => {
	// Only run during SSR. Running on client would duplicate the platform's fetch.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		return;
	}

	const { perimeter } = getSSRRenderInputs();

	if (perimeter === 'fedramp-moderate') {
		window.__WEB_STORAGE_CONSENT_CATEGORIES_LOCAL__ = null;
		window.__WEB_STORAGE_CONSENT_CATEGORIES_SESSION__ = null;
		return;
	}

	const needsLocal = !window.__WEB_STORAGE_CONSENT_CATEGORIES_LOCAL__;
	const needsSession = !window.__WEB_STORAGE_CONSENT_CATEGORIES_SESSION__;
	if (!needsLocal && !needsSession) {
		return;
	}

	return SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:WebStorageCategories', async () => {
		// fetchCategoriesSafely catches errors and returns null, so Promise.all never rejects
		// eslint-disable-next-line check-preloaders/no-promise-all -- safe: fetchCategoriesSafely never throws
		const [localResult, sessionResult] = await Promise.all([
			needsLocal ? fetchCategories(perimeter, 'LOCAL_STORAGE') : Promise.resolve(undefined),
			needsSession ? fetchCategories(perimeter, 'SESSION_STORAGE') : Promise.resolve(undefined),
		]);

		if (needsLocal) {
			window.__WEB_STORAGE_CONSENT_CATEGORIES_LOCAL__ = localResult ?? null;
		}
		if (needsSession) {
			window.__WEB_STORAGE_CONSENT_CATEGORIES_SESSION__ = sessionResult ?? null;
		}
	});
};
